import { useEffect, useMemo, useState } from "react";
import { ARENA, ARENA_WALLS, noteText, replayFrames, type Frame, type Replay } from "@buildlab/shared/arena";
import { arenaApi, type ArenaMatchInfo, type ArenaSide } from "./api";
import { Avatar } from "./Sprite";

// Bot Arena replays (decision 99): a match played again from its seed and moves with the rules
// (packages/shared/src/arena.ts), so no bot code runs here at all. Blue starts top left, red bottom
// right. A turn slider, play/pause and speed, and a caption for what just happened, big enough for
// the projector.

const WALLS = new Set(ARENA_WALLS.map((w) => w.y * ARENA.size + w.x));

/** The board at one moment. */
export function ArenaBoard({ frame, label }: { frame: Frame; label?: string }) {
  const cells = [];
  for (let y = 0; y < ARENA.size; y++)
    for (let x = 0; x < ARENA.size; x++) {
      const k = y * ARENA.size + x;
      const a = frame.bots[0].x === x && frame.bots[0].y === y;
      const b = frame.bots[1].x === x && frame.bots[1].y === y;
      const coin = frame.coins.some((c) => c.x === x && c.y === y);
      const grab = (a && frame.events?.grabbed[0]) || (b && frame.events?.grabbed[1]);
      cells.push(
        <span key={k} className={`arena-cell${WALLS.has(k) ? " wall" : ""}${a ? " bot-a" : ""}${b ? " bot-b" : ""}${grab ? " grab" : ""}`}>
          {a ? "🤖" : b ? "👾" : coin ? "🪙" : ""}
        </span>,
      );
    }
  return (
    <div className="arena-board" role="img" aria-label={label ?? `The board after turn ${frame.turn}`}>
      {cells}
    </div>
  );
}

/** What happened on this turn, in a few words. */
function caption(f: Frame, names: [string, string]): string {
  if (f.turn === 0) return "Ready… go!";
  const e = f.events!;
  const bits: string[] = [];
  if (e.bumped) bits.push("Bump! Neither bot could move.");
  e.grabbed.forEach((g, i) => g && bits.push(`${names[i]} grabbed a coin!`));
  e.blocked.forEach((b, i) => b && bits.push(`${names[i]} walked into a wall.`));
  if (f.turn === ARENA.turns) bits.push("Time's up!");
  const did = (d: string) => (d === "stay" ? "stayed put" : `went ${d}`);
  return bits.join(" ") || `${names[0]} ${did(f.moves![0])}, ${names[1]} ${did(f.moves![1])}.`;
}

const SPEEDS = [
  { label: "Slow", ms: 650 },
  { label: "Normal", ms: 300 },
  { label: "Fast", ms: 110 },
];

function Player({ side, color, coins, winner }: { side: ArenaSide; color: "a" | "b"; coins: number; winner: boolean }) {
  return (
    <div className={`arena-player p-${color}${winner ? " won" : ""}`}>
      <span className="arena-player-icon" aria-hidden="true">
        {color === "a" ? "🤖" : "👾"}
      </span>
      <span className="arena-player-who">
        <b>{side.botName}</b>
        <span className="small">
          {!side.house && <Avatar name={side.name} avatar={side.avatar} size={14} />}
          {side.name}
          {side.me ? " (you)" : ""}
        </span>
      </span>
      <span className="arena-player-coins" aria-label={`${coins} coins`}>
        {coins}
      </span>
    </div>
  );
}

/** A replay dialog for one match, loaded by id (or given straight from a tournament that just ran). */
export function ArenaReplay({ matchId, given, onClose }: { matchId?: string; given?: ArenaMatchInfo & { replay: Replay }; onClose: () => void }) {
  const [match, setMatch] = useState<(ArenaMatchInfo & { replay: Replay }) | null>(given ?? null);
  const [error, setError] = useState<string | null>(null);
  const [turn, setTurn] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState(1);

  useEffect(() => {
    if (given || !matchId) return;
    arenaApi.match(matchId).then((r) => setMatch(r.match), (e: Error) => setError(e.message));
  }, [matchId]);
  const frames = useMemo(() => (match ? replayFrames(match.replay) : []), [match]);

  useEffect(() => {
    if (!playing || !frames.length) return;
    if (turn >= frames.length - 1) {
      setPlaying(false);
      return;
    }
    const t = setTimeout(() => setTurn((n) => Math.min(n + 1, frames.length - 1)), SPEEDS[speed]!.ms);
    return () => clearTimeout(t);
  }, [playing, turn, frames.length, speed]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") setTurn((n) => Math.min(n + 1, ARENA.turns));
      else if (e.key === "ArrowLeft") setTurn((n) => Math.max(n - 1, 0));
      else if (e.key === " " && !(e.target instanceof HTMLButtonElement)) {
        e.preventDefault();
        setPlaying((p) => !p);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const f = frames[turn];
  const names: [string, string] = match ? [match.a.botName, match.b.botName] : ["Blue", "Red"];
  const over = turn === ARENA.turns;
  const notes = match?.replay.notes;
  return (
    <div className="golf-card-backdrop arena-replay-backdrop" role="dialog" aria-modal="true" aria-label="Replay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="arena-replay">
        <div className="row between arena-replay-head">
          <div>
            <span className="eyebrow">Bot Arena · {ARENA.title} replay</span>
            <h2>{match ? `${match.a.botName} vs ${match.b.botName}` : "Loading the match…"}</h2>
          </div>
          <button type="button" className="icon-close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
        {error && <p className="page-error">{error}</p>}
        {match && f && (
          <div className="arena-replay-body">
            <ArenaBoard frame={f} />
            <div className="arena-replay-side">
              <Player side={match.a} color="a" coins={f.bots[0].coins} winner={over && match.winner === "a"} />
              <Player side={match.b} color="b" coins={f.bots[1].coins} winner={over && match.winner === "b"} />
              <p className="arena-turn" aria-live="off">
                Turn <b>{turn}</b> of {ARENA.turns}
              </p>
              <p className="arena-caption" data-testid="arena-caption">
                {over ? (match.winner === "draw" ? "It's a draw!" : `${names[match.winner === "a" ? 0 : 1]} wins!`) : caption(f, names)}
              </p>
              {(notes?.a || notes?.b) && (
                <ul className="arena-notes small">
                  {notes?.a && (
                    <li>
                      <b>{match.a.botName}:</b> {noteText(notes.a)}
                    </li>
                  )}
                  {notes?.b && (
                    <li>
                      <b>{match.b.botName}:</b> {noteText(notes.b)}
                    </li>
                  )}
                </ul>
              )}
            </div>
            <div className="arena-controls">
              <button type="button" className="primary" onClick={() => (over ? (setTurn(0), setPlaying(true)) : setPlaying(!playing))}>
                {over ? "Watch again" : playing ? "Pause" : "Play"}
              </button>
              <button type="button" className="outline arena-step" aria-label="One turn back" disabled={turn === 0} onClick={() => (setPlaying(false), setTurn(turn - 1))}>
                ◀
              </button>
              <button type="button" className="outline arena-step" aria-label="One turn forward" disabled={over} onClick={() => (setPlaying(false), setTurn(turn + 1))}>
                ▶
              </button>
              <input
                type="range"
                className="arena-slider"
                min={0}
                max={ARENA.turns}
                value={turn}
                aria-label="Turn"
                onChange={(e) => {
                  setPlaying(false);
                  setTurn(Number(e.target.value));
                }}
              />
              <label className="arena-speed small">
                Speed{" "}
                <select value={speed} onChange={(e) => setSpeed(Number(e.target.value))}>
                  {SPEEDS.map((s, i) => (
                    <option key={s.label} value={i}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
